import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { sidebarPermissionMap } from '../permissions';
import { globalSearch } from '../services/api';

type MenuItem = { path: string; icon: string; key: string };

const dashboardItem: MenuItem = { path: '/', icon: '📊', key: 'dashboard' };

const menuGroups: { titleKey: string; items: MenuItem[] }[] = [
  {
    titleKey: 'groupMarketing',
    items: [
      { path: '/leads', icon: '🎯', key: 'leads' },
      { path: '/prospects', icon: '📋', key: 'prospects' },
      { path: '/registration-forms', icon: '📝', key: 'registrationForms' },
    ],
  },
  {
    titleKey: 'groupAcademic',
    items: [
      { path: '/students', icon: '🎓', key: 'students' },
      { path: '/courses', icon: '📚', key: 'courses' },
      { path: '/schedule', icon: '🗓️', key: 'schedule' },
      { path: '/lecturers', icon: '👨‍🏫', key: 'lecturers' },
    ],
  },
  {
    titleKey: 'groupManagement',
    items: [
      { path: '/finance', icon: '💰', key: 'finance' },
      { path: '/accounting', icon: '📑', key: 'accounting' },
      { path: '/tasks', icon: '✅', key: 'tasks' },
      { path: '/communication', icon: '📨', key: 'communication' },
      { path: '/reports', icon: '📈', key: 'reports' },
      { path: '/settings', icon: '⚙️', key: 'settings' },
    ],
  },
];

const roleLabels: Record<string, Record<string, string>> = {
  admin: { he: 'מנהל', ar: 'مدير' },
  secretary: { he: 'מזכירה', ar: 'سكرتيرة' },
  lecturer: { he: 'מרצה', ar: 'محاضر' },
  viewer: { he: 'צופה', ar: 'مشاهد' },
};

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang, fontFamily } = useLang();
  const { user, permissions, logout } = useUser();

  const [searchQ, setSearchQ] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<any | null>(null);
  const [quickAddOpen, setQuickAddOpen] = React.useState(false);

  React.useEffect(() => {
    document.body.className = lang === 'ar' ? 'lang-ar' : '';
    document.body.style.fontFamily = fontFamily;
  }, [lang, fontFamily]);

  const canView = (key: string) => {
    const permKey = sidebarPermissionMap[key];
    if (!permKey || !permissions) return false;
    return permissions[permKey]?.view;
  };

  const roleName = roleLabels[user.role]?.[lang === 'ar' ? 'ar' : 'he'] || user.role;

  const handleSearchChange = (value: string) => {
    setSearchQ(value);
    if (value.length >= 2) {
      globalSearch(value).then(r => setSearchResults(r.data)).catch(() => setSearchResults(null));
    } else {
      setSearchResults(null);
    }
  };

  const handleResultClick = (route: string) => {
    navigate(route);
    setSearchQ('');
    setSearchResults(null);
  };

  const quickAddActions = [
    { key: 'quickAddLead', enabled: permissions?.leads?.add, route: '/leads?new=1' },
    { key: 'quickAddProspect', enabled: permissions?.prospects?.add, route: '/prospects?new=1' },
    { key: 'quickAddTask', enabled: permissions?.tasks?.add, route: '/tasks?new=1' },
  ].filter(a => a.enabled);

  const resultSections: { type: string; labelKey: string }[] = [
    { type: 'students', labelKey: 'students' },
    { type: 'leads', labelKey: 'leads' },
    { type: 'prospects', labelKey: 'prospects' },
  ];

  const hasAnyResults =
    searchResults &&
    resultSections.some(s => (searchResults[s.type] || []).length > 0);

  return (
    <div className="app-container" style={{ fontFamily }}>
      {/* Sidebar */}
      <aside className="sidebar" style={{ fontFamily }}>
        <div className="sidebar-header">
          <div className="sidebar-logo">MANARA</div>
          <div className="sidebar-slogan">{t('slogan')}</div>
        </div>
        <nav className="sidebar-nav">
          {/* Dashboard (ungrouped, always visible) */}
          <Link
            key={dashboardItem.path}
            to={dashboardItem.path}
            className={`sidebar-link ${location.pathname === dashboardItem.path ? 'active' : ''}`}
          >
            <span className="icon">{dashboardItem.icon}</span>
            <span>{t(dashboardItem.key)}</span>
          </Link>

          {menuGroups.map(group => {
            const items = group.items.filter(item => canView(item.key));
            if (items.length === 0) return null;
            return (
              <div key={group.titleKey}>
                <div
                  style={{
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    margin: '16px 12px 6px',
                  }}
                >
                  {t(group.titleKey)}
                </div>
                {items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    <span className="icon">{item.icon}</span>
                    <span>{t(item.key)}</span>
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Top Header */}
      <header className="top-header" style={{ fontFamily }}>
        <div className="header-right">
          <div className="header-brand-block">
            <div className="header-brand">MANARA</div>
            <div className="header-slogan">מכללת מנארה</div>
          </div>
        </div>

        {/* Global search */}
        <div className="header-center" style={{ position: 'relative' }}>
          <input
            type="text"
            dir="auto"
            value={searchQ}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder={t('globalSearchPlaceholder')}
            style={{
              width: 260,
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.3)',
              fontFamily,
            }}
          />
          {searchQ.length >= 2 && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                insetInlineStart: 0,
                width: 260,
                background: '#fff',
                color: '#222',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                borderRadius: 6,
                zIndex: 1000,
                maxHeight: 320,
                overflow: 'auto',
                marginTop: 4,
              }}
            >
              {hasAnyResults ? (
                resultSections.map(section => {
                  const items = (searchResults[section.type] || []) as any[];
                  if (items.length === 0) return null;
                  return (
                    <div key={section.type}>
                      <div
                        style={{
                          fontSize: 11,
                          textTransform: 'uppercase',
                          color: '#888',
                          padding: '6px 10px 2px',
                        }}
                      >
                        {t(section.labelKey)}
                      </div>
                      {items.map(item => (
                        <div
                          key={`${section.type}-${item.id}`}
                          onClick={() => handleResultClick(item.route)}
                          style={{ padding: '6px 10px', cursor: 'pointer' }}
                        >
                          {item.name} — {item.phone}
                        </div>
                      ))}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '10px', color: '#888' }}>{t('searchNoResults')}</div>
              )}
            </div>
          )}
        </div>

        <div className="header-left">
          <span className="user-name">
            👤 {lang === 'ar' ? user.nameAr || user.name : user.name}
            <span style={{ fontSize: 11, opacity: 0.8, marginRight: 6 }}>({roleName})</span>
          </span>

          {/* Quick add */}
          {quickAddActions.length > 0 && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                className="lang-btn"
                onClick={() => setQuickAddOpen(o => !o)}
                style={{ fontFamily }}
                title={t('quickAdd')}
              >
                ＋ {t('quickAdd')}
              </button>
              {quickAddOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    insetInlineEnd: 0,
                    background: '#fff',
                    color: '#222',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    borderRadius: 6,
                    zIndex: 1000,
                    marginTop: 4,
                    minWidth: 160,
                  }}
                >
                  {quickAddActions.map(action => (
                    <div
                      key={action.key}
                      onClick={() => {
                        navigate(action.route);
                        setQuickAddOpen(false);
                      }}
                      style={{ padding: '8px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {t(action.key)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            className="lang-btn"
            onClick={logout}
            style={{ fontFamily, background: 'rgba(220,53,69,0.3)', borderColor: 'rgba(220,53,69,0.5)' }}
          >
            יציאה 🚪
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content" style={{ fontFamily }}>
        {children}
      </main>
    </div>
  );
}
